import React, { useEffect, useRef } from "react";

// A subtle-but-obvious particle field that "bursts" away from the cursor
// and then reforms. Runs entirely on the client; pointer-events disabled.
// Tuned for dark backgrounds + green brand accent.

export default function ParticleBurstCanvas({
  className,
  density = 220,
  accent = "rgba(0, 255, 133, 0.92)",
  neutral = "rgba(255, 255, 255, 0.30)",
}) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    w: 0,
    h: 0,
    dpr: 1,
    mouse: { x: -9999, y: -9999, active: false },
    particles: [],
    raf: 0,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });

    const resize = () => {
      const { clientWidth, clientHeight } = canvas;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.floor(clientWidth * dpr);
      canvas.height = Math.floor(clientHeight * dpr);

      stateRef.current.w = clientWidth;
      stateRef.current.h = clientHeight;
      stateRef.current.dpr = dpr;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Re-seed particles when size changes significantly.
      seed();
    };

    const seed = () => {
      const s = stateRef.current;
      const w = s.w;
      const h = s.h;

      // Scatter anchors across the viewport so interaction feels site-wide.
      // Still slightly left-biased to match the reading layout.
      const anchors = [
        { x: w * 0.18, y: h * 0.18 },
        { x: w * 0.42, y: h * 0.20 },
        { x: w * 0.68, y: h * 0.24 },
        { x: w * 0.26, y: h * 0.44 },
        { x: w * 0.56, y: h * 0.46 },
        { x: w * 0.80, y: h * 0.52 },
        { x: w * 0.22, y: h * 0.70 },
        { x: w * 0.52, y: h * 0.74 },
        { x: w * 0.78, y: h * 0.78 },
      ];

      const next = [];
      for (let i = 0; i < density; i += 1) {
        const a = anchors[i % anchors.length];
        const r = 24 + Math.random() * 190;
        const t = Math.random() * Math.PI * 2;
        const ax = a.x + Math.cos(t) * r;
        const ay = a.y + Math.sin(t) * r;

        next.push({
          x: ax + (Math.random() - 0.5) * 260,
          y: ay + (Math.random() - 0.5) * 220,
          vx: 0,
          vy: 0,
          ax,
          ay,
          // size and alpha
          s: 0.8 + Math.random() * 1.9,
          a: 0.25 + Math.random() * 0.7,
          // every ~4th is accent
          c: i % 4 === 0 ? "accent" : "neutral",
        });
      }

      s.particles = next;
    };

    const onMove = (e) => {
      stateRef.current.mouse = {
        x: e.clientX,
        y: e.clientY,
        active: true,
      };
    };

    const onLeave = () => {
      stateRef.current.mouse.active = false;
    };

    const step = () => {
      const s = stateRef.current;
      const { w, h, particles, mouse } = s;

      ctx.clearRect(0, 0, w, h);

      // mild vignette to make particles feel embedded in the page
      const g = ctx.createRadialGradient(w * 0.24, h * 0.2, 40, w * 0.24, h * 0.2, Math.max(w, h) * 0.9);
      g.addColorStop(0, "rgba(0, 0, 0, 0)");
      g.addColorStop(1, "rgba(0, 0, 0, 0.55)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      const mx = mouse.x;
      const my = mouse.y;

      // physics constants (tuned for "burst then reform")
      const spring = 0.010; // attraction back to anchor
      const friction = 0.90;
      const repelRadius = 190;
      const repelStrength = 2.55;

      for (const p of particles) {
        // spring back to anchor
        const dxA = p.ax - p.x;
        const dyA = p.ay - p.y;
        p.vx += dxA * spring;
        p.vy += dyA * spring;

        // cursor repulsion
        if (mouse.active) {
          const dx = p.x - mx;
          const dy = p.y - my;
          const d2 = dx * dx + dy * dy;
          const r2 = repelRadius * repelRadius;
          if (d2 < r2) {
            const d = Math.max(Math.sqrt(d2), 0.001);
            const force = (1 - d / repelRadius) * repelStrength;
            p.vx += (dx / d) * force;
            p.vy += (dy / d) * force;
          }
        }

        // integrate
        p.vx *= friction;
        p.vy *= friction;
        p.x += p.vx;
        p.y += p.vy;

        // soft bounds
        if (p.x < -40) p.x = w + 40;
        if (p.x > w + 40) p.x = -40;
        if (p.y < -40) p.y = h + 40;
        if (p.y > h + 40) p.y = -40;

        // draw
        ctx.beginPath();
        ctx.fillStyle = p.c === "accent" ? accent : neutral;
        ctx.globalAlpha = p.a;
        ctx.arc(p.x, p.y, p.s, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      s.raf = window.requestAnimationFrame(step);
    };

    // init
    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave);

    const state = stateRef.current;
    state.raf = window.requestAnimationFrame(step);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      window.cancelAnimationFrame(state.raf);
    };
  }, [density, accent, neutral]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: "100%", height: "100%" }}
    />
  );
}
